<script setup lang="ts">
import { ref, defineEmits, withDefaults, watch } from 'vue'
import { isIOS } from "@vueuse/core"
import {
  Player,
  Vimeo,
  Youtube,
  Ui,
  ClickToPlay,
  Video
} from "@vime/vue-next"

interface ItemsObj {
  [key: string]: any
}

type VideoProps = {
  playerSettings?: ItemsObj
  videoSettings?: ItemsObj
  videoSrc?: string
  sourceType?: string
  clickToPlay?: boolean
  clickToPlaySettings?: ItemsObj
  vimeoId?: string
  vimeoSettings?: ItemsObj
  youtubeId?: string
  youtubeSettings?: ItemsObj
  setPause?: boolean
}

const props = withDefaults(defineProps<VideoProps>(), {
  sourceType: 'video/mp4',
  playerSettings: () => ({
    controls: true,
  }),
  clickToPlay: true,
  clickToPlaySettings: () => ({ useOnMobile: true }),
})
const emit = defineEmits(['ready', 'pauseChanged', 'play', 'controlsChange'])

const player = ref(null)
const isPlaying = ref(false)

watch(
  () => props.setPause,
  (newVal) => {
    if (newVal) {
      player.value.pause()
    }
  },
)

const ready = () => {
  emit('ready', player.value)
}

const controlsChange = () => {
  emit('controlsChange', player.value)
}

const pauseChanged = () => {
  isPlaying.value = !isPlaying.value
  emit('pauseChanged', isPlaying.value)
}

const play = () => {
  isPlaying.value = true
  emit('play', true)
}
</script>

<template>
  <div class="app-video">
    <div class="app-video__wrapper">
      <Player
        ref="player"
        v-bind="props.playerSettings"
        :autoplay="false"
        @vmReady="ready"
        @vmPausedChange="pauseChanged"
        @vmControlsChange="controlsChange"
        @vmPlay="play"
        class="app-video__player"
      >
        <Video
          v-if="props.videoSrc"
          class="app-video__video"
          v-bind="props.videoSettings"
        >
          <source :data-src="props.videoSrc" :type="props.sourceType" />
        </Video>
        <Vimeo
          v-if="props.vimeoId"
          :video-id="props.vimeoId"
          class="app-video__vimeo"
          v-bind="props.vimeoSettings"
        />
        <Youtube
          v-if="props.youtubeId"
          class="app-video__youtube"
          :video-id="props.youtubeId"
          v-bind="props.youtubeSettings"
        />
        <Ui :class="{ 'app-video__ui': !isIOS }">
          <slot name="ui"></slot>
          <ClickToPlay
            v-if="props.clickToPlay"
            v-bind="props.clickToPlaySettings"
          />
        </Ui>
      </Player>
    </div>
  </div>
</template>

<style scoped>

@import '@vime/core/themes/default.css';
@import '@vime/core/themes/light.css';

</style>
